/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable max-lines */
import React from 'react'
import { tagAxes, TagAxis, tagAxesObject, TagAxisId, extractDetailedTag } from '@cpsy-rmap-project/tags'
import { DetailedPaper } from '@cpsy-rmap-project/web-core/repositories'
import Fuse from 'fuse.js'
import { uniq, intersection, union, difference, compact, isEmpty, flatten, isEqual } from 'lodash'
import { useDeepCompareEffect, usePrevious } from 'react-use'
import { LoadingOverlay } from '../../atoms/Loading'
import { HandleSelectTagsOption } from '../../molecules/MultipleSelectTags'
import { usePaper } from '../../organisms/Paper'

export const FilteringWithDraft = {
  ALL: 'ALL',
  ONLY_DRAFT: 'ONLY_DRAFT',
  ONLY_REGISTRATION: 'ONLY_REGISTRATION'
} as const
export type FilteringWithDraft = typeof FilteringWithDraft[keyof typeof FilteringWithDraft]
export const filteringWithDraftLabelMap: {
  [x in FilteringWithDraft]: string
} = {
  ALL: 'All',
  ONLY_DRAFT: 'Automatic Registration',
  ONLY_REGISTRATION: 'Human Registration'
}
export interface HeatMapCellPapersData {
  verticalTagId: string
  horizontalTagId: string
  papers: DetailedPaper[]
}

type HandleFilterTagsV2Option = HandleSelectTagsOption

interface CprmSearchProps {
  verticalAxis: TagAxis['id']
  horizontalAxis: TagAxis['id']
  freewords: string
  isSearched: boolean
  isResetted: boolean
  filteringWithDraft: FilteringWithDraft
  filterTagsV2: Record<TagAxis['id'], string[]>
  excludeTagsV2: Record<TagAxis['id'], string[]>
  hideUnspecifiedTags: boolean
  selectHeatMapCellTags: string[] /** tagIds */
  papers: DetailedPaper[]
  filterdPapers: DetailedPaper[]
  heatMapFilterdPapers: DetailedPaper[]
  cellMap: HeatMapCellPapersData[][]
  handleVerticalAxis: (tagAxis: TagAxis['id']) => void
  handleHorizontalAxis: (tagAxis: TagAxis['id']) => void
  handleFreewords: (freewords: string) => void
  handleIsSearched: (isSearched: boolean) => void
  handleFilteringWithDraft: (filteringWithDraft: FilteringWithDraft) => void
  handleFilterTagsV2: (tagAxis: TagAxis['id'], options: HandleFilterTagsV2Option) => void
  handleExcludeTagsV2: (tagAxis: TagAxis['id'], options: HandleFilterTagsV2Option) => void
  handleHideUnspecifiedTags: (_: boolean) => void
  handleSelectHeatMapCellTags: (selectHeatMapCellTags: string[] /** tagIds */) => void
  handleReset: () => void
}

const defaultVerticalAxis = 'rdocConstructs' as const
const defaultHorizontalAxis = 'unitOfAnalysis' as const

const CprmSearchInitState: CprmSearchProps = {
  verticalAxis: defaultVerticalAxis,
  horizontalAxis: defaultHorizontalAxis,
  freewords: '',
  isSearched: false,
  isResetted: true,
  filteringWithDraft: 'ALL',
  filterTagsV2: {
    rdocConstructs: [],
    unitOfAnalysis: [],
    dsm5: [],
    compMathMethodExperimentalDesign: [],
    compMathMethodDataType: [],
    compMathMethodModels: [],
    symptomatology: []
  },
  excludeTagsV2: {
    rdocConstructs: [],
    unitOfAnalysis: [],
    dsm5: [],
    compMathMethodExperimentalDesign: [],
    compMathMethodDataType: [],
    compMathMethodModels: [],
    symptomatology: []
  },
  hideUnspecifiedTags: true,
  selectHeatMapCellTags: [],
  papers: [],
  filterdPapers: [],
  heatMapFilterdPapers: [],
  cellMap: [],
  handleVerticalAxis: () => {},
  handleHorizontalAxis: () => {},
  handleFreewords: () => {},
  handleIsSearched: () => {},
  handleFilteringWithDraft: () => {},
  handleFilterTagsV2: () => {},
  handleExcludeTagsV2: () => {},
  handleHideUnspecifiedTags: () => {},
  handleSelectHeatMapCellTags: () => {},
  handleReset: () => {}
}

const CprmSearch = React.createContext<CprmSearchProps>(CprmSearchInitState)

const searchOptions: Fuse.IFuseOptions<DetailedPaper> = {
  threshold: 0.3,
  distance: 100,
  minMatchCharLength: 1,
  // NOTE: keyやあいまい度などに応じて修正して下さい
  keys: ['title', 'authors', 'journal', 'year', 'abstract', 'hypothesis', 'prediction', 'relevantPapers', 'comment']
}

// eslint-disable-next-line max-lines-per-function
export const CprmSearchProvider: React.FC = ({ children }) => {
  const { papers: allPapers, papersMap: allPapersMap, forFilter } = usePaper()

  // リセット検知用
  const [isResetting, setIsResetting] = React.useState<boolean>(false)
  const [isResetted, setIsResetted] = React.useState<boolean>(CprmSearchInitState.isResetted)

  // フリーワード検索用
  const [freewords, setFreewords] = React.useState<string>(CprmSearchInitState.freewords)
  const [isSearched, setIsSearched] = React.useState<boolean>(CprmSearchInitState.isSearched)

  // 仮登録表示
  const [filteringWithDraft, setFilteringWithDraft] = React.useState<FilteringWithDraft>('ALL')
  const handleFilteringWithDraft = React.useCallback((_filteringWithDraft: FilteringWithDraft) => {
    setFilteringWithDraft(_filteringWithDraft)
  }, [])

  const [freewordsFilterdPaperIds, setFreewordsFilterdPaperIds] = React.useState<string[] | undefined>([])
  const [searching, setSearching] = React.useState<boolean>(false)
  React.useEffect(() => {
    if (freewords === '') {
      setFreewordsFilterdPaperIds(undefined)
    } else {
      setSearching(true)
      setTimeout(() => {
        const fuse = new Fuse(allPapers, searchOptions)
        const fuseSearchResult = fuse.search(freewords).map(d => d.item.id)
        const _freewordsFilterdPaperIds = freewords === '' ? undefined : fuseSearchResult
        setFreewordsFilterdPaperIds(_freewordsFilterdPaperIds)
        setSearching(false)
      })
    }
  }, [freewords, allPapers])

  const papers = allPapers

  const handleFreewords: CprmSearchProps['handleFreewords'] = _freewords => {
    setFreewords(_freewords)
    setIsResetted(false) // リセットフラグを更新
  }

  const handleIsSearched: CprmSearchProps['handleIsSearched'] = _isSearched => {
    setIsSearched(_isSearched)
  }

  // 検索画面でFilterされたPapers
  const [filterdPapers, setFilterdPapers] = React.useState<DetailedPaper[]>(papers)
  const [filterdPaperIds, setFilterdPaperIds] = React.useState<string[] | undefined>()

  // HeatMapで選択されたPapers（未選択のときはそのままfilterdPapers）
  const [heatMapFilterdPapers, setHeatMapFilterdPapers] = React.useState<DetailedPaper[]>(filterdPapers)

  const [verticalAxis, setVerticalAxis] = React.useState<CprmSearchProps['verticalAxis']>(
    CprmSearchInitState.verticalAxis
  )
  const [horizontalAxis, setHorizontalAxis] = React.useState<CprmSearchProps['horizontalAxis']>(
    CprmSearchInitState.horizontalAxis
  )

  const [filterTagsV2, setFilterTagsV2] = React.useState<Record<TagAxis['id'], string[]>>(
    CprmSearchInitState.filterTagsV2
  )
  const [excludeTagsV2, setExcludeTagsV2] = React.useState<Record<TagAxis['id'], string[]>>(
    CprmSearchInitState.excludeTagsV2
  )
  const [selectHeatMapCellTags, setSelectHeatMapCellTags] = React.useState<string[]>(
    CprmSearchInitState.selectHeatMapCellTags
  )

  /**
   * Unspecifiedタグ非表示
   */
  const [hideUnspecifiedTags, setHideUnspecifiedTags] = React.useState(CprmSearchInitState.hideUnspecifiedTags)
  const prevVerticalAxis = usePrevious(verticalAxis)
  const prevHorizontalAxis = usePrevious(horizontalAxis)
  const hideUnspecifiedTagsTrigger = React.useMemo(
    () => [hideUnspecifiedTags, verticalAxis, horizontalAxis],
    [hideUnspecifiedTags, horizontalAxis, verticalAxis]
  ) // ボタン、軸が変わったタイミングだけ発動させる
  const prevHideUnspecifiedTagsTrigger = usePrevious(hideUnspecifiedTagsTrigger)
  React.useEffect(() => {
    if (isEqual(hideUnspecifiedTagsTrigger, prevHideUnspecifiedTagsTrigger)) return

    if (hideUnspecifiedTags) {
      /**
       * hideUnspecifiedTagsがONになった時
       */

      const isIncludedPrevVerticalAxis = prevVerticalAxis
        ? [verticalAxis, horizontalAxis].includes(prevVerticalAxis)
        : false
      const isIncludedPrevHorizontalAxis = prevHorizontalAxis
        ? [verticalAxis, horizontalAxis].includes(prevHorizontalAxis)
        : false

      const nextExcludeTagsV2 = {
        ...excludeTagsV2,

        // vertilcal軸が変わった時
        ...(prevVerticalAxis
          ? {
              [prevVerticalAxis]: isIncludedPrevVerticalAxis
                ? excludeTagsV2[prevVerticalAxis]
                : excludeTagsV2[prevVerticalAxis].filter(id => id !== unspecifiedTagIds[prevVerticalAxis])
            }
          : {}),
        // horizontal軸が変わった時
        ...(prevHorizontalAxis
          ? {
              [prevHorizontalAxis]: isIncludedPrevHorizontalAxis
                ? excludeTagsV2[prevHorizontalAxis]
                : excludeTagsV2[prevHorizontalAxis].filter(id => id !== unspecifiedTagIds[prevHorizontalAxis])
            }
          : {}),
        // 現在の軸に対しての処理
        [verticalAxis]: excludeTagsV2[verticalAxis].includes(unspecifiedTagIds[verticalAxis])
          ? excludeTagsV2[verticalAxis]
          : [...excludeTagsV2[verticalAxis], unspecifiedTagIds[verticalAxis]],
        [horizontalAxis]: excludeTagsV2[horizontalAxis].includes(unspecifiedTagIds[horizontalAxis])
          ? excludeTagsV2[horizontalAxis]
          : [...excludeTagsV2[horizontalAxis], unspecifiedTagIds[horizontalAxis]]
      }
      if (!isEqual(excludeTagsV2, nextExcludeTagsV2)) {
        setExcludeTagsV2(nextExcludeTagsV2)
      }
    } else {
      /**
       * hideUnspecifiedTagsがOFFになった時
       */
      const nextExcludeTagsV2 = {
        ...excludeTagsV2,
        [verticalAxis]: excludeTagsV2[verticalAxis].includes(unspecifiedTagIds[verticalAxis])
          ? excludeTagsV2[verticalAxis].filter(id => id !== unspecifiedTagIds[verticalAxis])
          : excludeTagsV2[verticalAxis],
        [horizontalAxis]: excludeTagsV2[horizontalAxis].includes(unspecifiedTagIds[horizontalAxis])
          ? excludeTagsV2[horizontalAxis].filter(id => id !== unspecifiedTagIds[horizontalAxis])
          : excludeTagsV2[horizontalAxis]
      }
      if (!isEqual(excludeTagsV2, nextExcludeTagsV2)) {
        setExcludeTagsV2(nextExcludeTagsV2)
      }
    }
  }, [
    excludeTagsV2,
    hideUnspecifiedTags,
    hideUnspecifiedTagsTrigger,
    horizontalAxis,
    prevHideUnspecifiedTagsTrigger,
    prevHorizontalAxis,
    prevVerticalAxis,
    verticalAxis
  ])

  //

  const handleFilterTagsV2 = React.useCallback(
    (tagAxis: TagAxis['id'], tagIds: HandleFilterTagsV2Option) => {
      setFilterTagsV2({
        ...filterTagsV2,
        [tagAxis]: tagIds
      })
      setIsResetted(false) // リセットフラグを更新
    },
    [filterTagsV2]
  )
  const handleExcludeTagsV2 = React.useCallback(
    (tagAxis: TagAxis['id'], tagIds: HandleFilterTagsV2Option) => {
      setExcludeTagsV2({
        ...excludeTagsV2,
        [tagAxis]: tagIds
      })
      setIsResetted(false) // リセットフラグを更新
    },
    [excludeTagsV2]
  )

  const handleSelectHeatMapCellTags = React.useCallback((newSelectHeatMapCellTags: string[]) => {
    setSelectHeatMapCellTags(newSelectHeatMapCellTags)

    // リセットフラグを更新
    setIsResetted(false)
  }, [])

  const handleVerticalAxis = React.useCallback(
    (tagAxis: TagAxis['id']) => {
      setVerticalAxis(tagAxis)
      handleSelectHeatMapCellTags([])

      // リセットフラグを更新
      setIsResetted(false)
    },
    [handleSelectHeatMapCellTags]
  )

  const handleHorizontalAxis = React.useCallback(
    (tagAxis: TagAxis['id']) => {
      setHorizontalAxis(tagAxis)
      handleSelectHeatMapCellTags([])

      // リセットフラグを更新
      setIsResetted(false)
    },
    [handleSelectHeatMapCellTags]
  )

  // 変更があったら変更する
  useDeepCompareEffect(() => {
    /**
     * 絞り込み用IDの抽出
     */

    // フリーワードで絞り込みは事前にhooksで行う
    // const freewordsFilterdPaperIds

    // ドラフトStatusで絞り込み
    const draftStatusFilterdPaperIds = (() => {
      switch (filteringWithDraft) {
        case 'ALL':
          return undefined
        case 'ONLY_DRAFT':
          return forFilter.draftStatusFilterdPaperIds.draft
        case 'ONLY_REGISTRATION':
          return forFilter.draftStatusFilterdPaperIds.registration
        default:
          throw new Error() // 想定外
      }
    })()

    // タグフィルタで絞り込み
    const __allPaperIds = allPapers.map(p => p.id)
    const filterTagIds = flatten(compact(tagAxes.map(tagAxis => filterTagsV2[tagAxis.id])))
    const __tagAxesFilterdPaperIds = tagAxes.map(tagAxis =>
      union(...filterTagsV2[tagAxis.id].map(tagId => forFilter.eachTagIndexes[tagId]))
    ) // カテゴリ内はunionで和集合
    const __tagFilterdPaperIds = isEmpty(filterTagIds)
      ? undefined
      : intersection(...compact(__tagAxesFilterdPaperIds.map(ids => (isEmpty(ids) ? undefined : ids)))) // カテゴリ外はintersectionで積集合
    const excludeTagIds = flatten(compact(tagAxes.map(tagAxis => excludeTagsV2[tagAxis.id])))
    const __tagExcludedPaperIds = isEmpty(excludeTagIds)
      ? undefined
      : union(...excludeTagIds.map(tagId => forFilter.eachTagIndexes[tagId])) // 除外はunionで和集合

    // HeatMapフィルタで絞り込み
    const filterHeatMapIds = selectHeatMapCellTags
    const __heatMapFilterdPaperIds = isEmpty(filterHeatMapIds)
      ? undefined
      : intersection(...filterHeatMapIds.map(tagId => forFilter.eachTagIndexes[tagId]))

    console.log('📝 DONE: 絞り込み用IDの抽出')

    //

    /**
     * 絞り込み用ID群→絞り込み用IDへ変換
     */

    const _filterdPaperIds = (() => {
      if (!freewordsFilterdPaperIds && !draftStatusFilterdPaperIds && !__tagFilterdPaperIds && !__tagExcludedPaperIds) {
        return undefined
      }

      const __intersectionedPaperIds = uniq(
        compact(
          intersection(
            __allPaperIds,
            ...(freewordsFilterdPaperIds ? [freewordsFilterdPaperIds] : []),
            ...(draftStatusFilterdPaperIds ? [draftStatusFilterdPaperIds] : []),
            ...(__tagFilterdPaperIds ? [__tagFilterdPaperIds] : [])
          )
        )
      )

      if (__tagExcludedPaperIds === undefined) {
        // 除外なし
        return __intersectionedPaperIds
      }
      // 除外あり
      if (isEmpty(__intersectionedPaperIds)) {
        // 積集合（intersection）が空の時は除外もクソも無いので空を返す
        return []
      }
      return difference(__intersectionedPaperIds, __tagExcludedPaperIds)
    })()

    const _heatMapFilterdPaperIds = (() => {
      if (!_filterdPaperIds && !__heatMapFilterdPaperIds) {
        return undefined
      }
      return uniq(
        compact(
          intersection(
            ...(_filterdPaperIds ? [_filterdPaperIds] : []),
            ...(__heatMapFilterdPaperIds ? [__heatMapFilterdPaperIds] : [])
          )
        )
      )
    })()

    setFilterdPaperIds(_filterdPaperIds)
    console.log('📝 DONE: 絞り込み用ID群→絞り込み用IDへ変換')

    //

    /**
     * 絞り込みID→絞り込み済みPapersへ変換
     */

    // FilteredPaper（HeatMap表示用）を更新する
    const _filterdPapers = _filterdPaperIds ? compact(_filterdPaperIds.map(id => allPapersMap.get(id))) : allPapers
    setFilterdPapers(_filterdPapers)

    // heatMapFilterdPapers（テーブル表示用）を更新する
    setHeatMapFilterdPapers(
      _heatMapFilterdPaperIds ? compact(_heatMapFilterdPaperIds.map(id => allPapersMap.get(id))) : allPapers
    )

    console.log('📝 DONE: 絞り込みID→絞り込み済みPapersへ変換')
  }, [
    allPapers,
    allPapersMap,
    filterTagsV2,
    excludeTagsV2,
    filteringWithDraft,
    forFilter,
    freewordsFilterdPaperIds,
    selectHeatMapCellTags
  ])

  //

  /**
   * CellMap毎回更新
   */
  const _verticalTagAxis = tagAxesObject[verticalAxis]
  const _horizontalTagAxis = tagAxesObject[horizontalAxis]
  const _verticalAxisTags = _verticalTagAxis.tags
  const _horizontalAxisTags = _horizontalTagAxis.tags

  const _cellMap: HeatMapCellPapersData[][] = _horizontalAxisTags.map(xTag =>
    _verticalAxisTags.map(yTag => {
      const _paperIds = uniq(
        compact(
          intersection(
            ...(filterdPaperIds ? [filterdPaperIds] : []),
            ...(forFilter.ancestorTag2DIndexes[xTag.id][yTag.id]
              ? [forFilter.ancestorTag2DIndexes[xTag.id][yTag.id]]
              : [])
          )
        )
      )
      const _papers = compact(_paperIds.map(id => allPapersMap.get(id)))

      return {
        verticalTagId: yTag.id,
        horizontalTagId: xTag.id,
        papers: _papers
      }
    })
  )

  const handleReset = React.useCallback(() => {
    setIsResetting(true)
  }, [])

  React.useEffect(() => {
    if (isResetting) {
      setVerticalAxis(CprmSearchInitState.verticalAxis)
      setHorizontalAxis(CprmSearchInitState.horizontalAxis)
      setFreewords(CprmSearchInitState.freewords)
      setFilterTagsV2(CprmSearchInitState.filterTagsV2)
      setExcludeTagsV2(CprmSearchInitState.excludeTagsV2)
      setSelectHeatMapCellTags(CprmSearchInitState.selectHeatMapCellTags)
      setFilteringWithDraft(CprmSearchInitState.filteringWithDraft)
      // unspecifiedタグの付与をずらす
      setHideUnspecifiedTags(false)
      setTimeout(() => {
        setHideUnspecifiedTags(true)
        setIsResetted(true)
        setIsResetting(false)
      })
    }
  }, [isResetting])

  return (
    <CprmSearch.Provider
      value={{
        verticalAxis,
        horizontalAxis,
        freewords,
        isSearched,
        isResetted,
        filteringWithDraft,
        filterTagsV2,
        excludeTagsV2,
        hideUnspecifiedTags,
        selectHeatMapCellTags,
        papers,
        filterdPapers,
        heatMapFilterdPapers,
        cellMap: _cellMap,
        handleVerticalAxis,
        handleHorizontalAxis,
        handleFreewords,
        handleIsSearched,
        handleFilteringWithDraft,
        handleFilterTagsV2,
        handleExcludeTagsV2,
        handleHideUnspecifiedTags: setHideUnspecifiedTags,
        handleSelectHeatMapCellTags,
        handleReset
      }}
    >
      {searching && <LoadingOverlay isLoading />}
      {isResetting && <LoadingOverlay isLoading />}
      {children}
    </CprmSearch.Provider>
  )
}

export const useCprmSearch = () => React.useContext(CprmSearch)

//

/** Unspecifiedなタグのみ抽出（Axis毎） */
const unspecifiedTagIds: { [x in TagAxisId]: string } = {
  rdocConstructs: extractDetailedTag('rdocConstructs', 'Unspecified')?.id!,
  unitOfAnalysis: extractDetailedTag('unitOfAnalysis', 'Unspecified')?.id!,
  dsm5: extractDetailedTag('dsm5', 'Unspecified')?.id!,
  compMathMethodExperimentalDesign: extractDetailedTag('compMathMethodExperimentalDesign', 'Unspecified')?.id!,
  compMathMethodDataType: extractDetailedTag('compMathMethodDataType', 'Unspecified')?.id!,
  compMathMethodModels: extractDetailedTag('compMathMethodModels', 'Unspecified')?.id!,
  symptomatology: extractDetailedTag('symptomatology', 'Unspecified')?.id!
}
